import React from "react";
import Gleap from "gleap";
import CpLandingShell from "../../components/CpLandingShell/CpLandingShell";
import brandonImg from "@/assets/catalog/lions-pride/brandon-marshall-cutout.webp";
import {
  AwardIcon,
  BookOpenIcon,
  CalendarCheckIcon,
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  CrownIcon,
  PlusIcon,
  ShieldIcon,
  UserIcon,
  UsersIcon,
} from "./lionsPrideIcons";
import "./LionsPridePage.css";

const STAGES = ["Build", "Grow", "Lead", "Scale"] as const;

const brandonStyle: React.CSSProperties = {
  backgroundImage: `url(${brandonImg})`,
};

const openChat = () => Gleap.open();

const WHY_CARDS: {
  icon: React.ReactNode;
  title: string;
  body: string;
  tag: string;
}[] = [
  {
    icon: <CalendarCheckIcon />,
    title: "Monthly challenges",
    body: "Every month delivers a specific leadership challenge — difficult conversations, alignment, accountability, trust — applied in your real day-to-day.",
    tag: "12 months, monthly leadership frameworks",
  },
  {
    icon: <BookOpenIcon />,
    title: "Leader Guide",
    body: "You don’t just learn the framework — you teach it. Each month comes with a Leader Guide to run the same session with your team.",
    tag: "Teach-it-forward Leader Guide included",
  },
  {
    icon: <AwardIcon />,
    title: "Led by Brandon Marshall",
    body: "20+ years of leadership experience — grew revenue from $3M to $15M over seven years and doubled revenue in his first year as GM.",
    tag: "Virtual + in-person workshop options",
  },
  {
    icon: <UsersIcon />,
    title: "Community of leaders",
    body: "A cohort of committed owners and managers at similar stages, working through the same challenges at the same time.",
    tag: "Community of committed leaders",
  },
];

const HOW_IT_WORKS: { title: string; body: string }[] = [
  {
    title: "Apply to a cohort",
    body: "Submit an application — we match by stage, role, and goals to a cohort starting in the next quarter.",
  },
  {
    title: "Receive monthly challenges",
    body: "Each month delivers a leadership challenge designed to apply directly in your day-to-day operations.",
  },
  {
    title: "Apply with your team",
    body: "Teach the framework using the included Leader Guide. Your team builds the same muscle alongside you.",
  },
  {
    title: "Compound the change",
    body: "12 months of reinforcement creates leadership habits that hold under pressure — and a team trained to do the same.",
  },
];

const FAQS: { question: string; answer: React.ReactNode }[] = [
  {
    question: "Who is Lion’s Pride for?",
    answer: (
      <>
        Owners, GMs, and managers ready to lead at scale — particularly those{" "}
        <strong>stepping from doing the work to leading the work.</strong>
      </>
    ),
  },
  {
    question: "Is Lion’s Pride available on my membership?",
    answer: (
      <>
        Yes — at every stage. <strong>Group-Based</strong> Lion’s Pride is
        included with Scale membership and is available as an add-on at Build,
        Grow, and Lead. <strong>Individual-Based</strong> Lion’s Pride is
        available as an add-on at all four stages.
      </>
    ),
  },
  {
    question: "What’s the time commitment?",
    answer: (
      <>
        Roughly <strong>2 hours of cohort time per month</strong>, plus the
        application time with your own team. The Leader Guide is built to fit
        into existing meeting rhythms.
      </>
    ),
  },
  {
    question: "Is it virtual or in-person?",
    answer: (
      <>
        Cohorts run{" "}
        <strong>virtually with optional in-person workshops.</strong> Most
        members prefer the flexibility of virtual with periodic in-person deep
        work.
      </>
    ),
  },
  {
    question: "Who is Brandon Marshall?",
    answer: (
      <>
        <strong>20+ years of leadership experience</strong> — grew revenue from
        $3M to $15M over seven years and doubled revenue in his first year as
        GM.
      </>
    ),
  },
];

type TierKind = "add" | "inc";
const TIER_EM: Record<TierKind, string> = { add: "Add-on", inc: "Included" };

/** "Available on" row for a format card: each stage as an add-on or included. */
const FormatTiers: React.FC<{ kinds: TierKind[] }> = ({ kinds }) => (
  <div
    aria-label={`Available on: ${STAGES.map(
      (stage, i) => `${stage} (${TIER_EM[kinds[i]].toLowerCase()})`,
    ).join(", ")}`}
    className="lp-tiers"
  >
    <span className="lp-tiers-label">Available on</span>
    {STAGES.map((stage, i) => (
      <span className={`lp-tier ${kinds[i]}`} key={stage}>
        {kinds[i] === "inc" ? <CheckIcon /> : <PlusIcon />}
        {stage}
        <em>{TIER_EM[kinds[i]]}</em>
      </span>
    ))}
  </div>
);

const FormatList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="lp-fmt-list">
    {items.map((item) => (
      <li key={item}>
        <span className="ck">
          <CheckIcon />
        </span>
        {item}
      </li>
    ))}
  </ul>
);

const LionsPridePage: React.FC = () => (
  <CpLandingShell title="Lion’s Pride">
    <div className="lp-root">
      {/* ================= HERO ================= */}
      <section className="lp-hero">
        <svg
          aria-hidden="true"
          className="lp-hero-arcs"
          fill="none"
          viewBox="0 0 600 800"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M600 40 C 380 120, 260 320, 330 560 S 520 820, 600 780"
            fill="none"
            opacity=".7"
            stroke="#FFC857"
            strokeWidth="2"
          />
          <path
            d="M600 120 C 430 200, 330 380, 400 600 S 560 800, 620 760"
            fill="none"
            opacity=".25"
            stroke="#B20030"
            strokeWidth="2"
          />
        </svg>

        <div className="lp-wrap lp-hero-grid">
          <div>
            <div className="lp-cat-tags">
              <span className="lp-cat-tag primary">Peer Groups</span>
            </div>

            <h1 className="lp-wordmark">
              <span className="a">Lion’s</span> <span className="b">Pride</span>
            </h1>
            <p className="lp-lede">
              A 12-month leadership mentorship for owners and managers ready to
              lead at scale
            </p>

            <div aria-label="Who it’s for" className="lp-audience">
              <span className="lp-audience-label">Built for</span>
              <span className="lp-aud-chip">Owners</span>
              <span className="lp-aud-chip">GMs</span>
              <span className="lp-aud-chip">Managers</span>
              <span className="lp-aud-chip">All Trades</span>
            </div>

            <div
              aria-label="Available on: Build, Grow, Lead, Scale"
              className="lp-tiers"
            >
              <span className="lp-tiers-label">Available on</span>
              {STAGES.map((stage) => (
                <span className="lp-tier on" key={stage}>
                  <CheckIcon />
                  {stage}
                </span>
              ))}
            </div>

            <div className="lp-hero-cta">
              <button className="lp-btn" onClick={openChat} type="button">
                Apply to Lion’s Pride <span className="arrow">→</span>
              </button>
              <a className="lp-btn outline" href="#how-it-works">
                See how it works
              </a>
            </div>

            <div className="lp-hero-meta">
              <div className="item">
                <div className="ico">
                  <CalendarIcon />
                </div>
                <div className="lab">12-month cohort</div>
              </div>
              <div className="item">
                <div className="ico">
                  <ShieldIcon />
                </div>
                <div className="lab">Leadership training</div>
              </div>
              <div className="item">
                <div className="ico">
                  <UsersIcon />
                </div>
                <div className="lab">Personalized mentorship</div>
              </div>
            </div>
          </div>

          {/* Hero composition: Brandon on stage + coach badge + stat chip */}
          <div aria-hidden="true" className="lp-hero-visual">
            <div className="lp-brandon-panel">
              <span className="arc" />
            </div>
            <div className="lp-brandon-photo">
              <div
                aria-label="Brandon Marshall speaking on stage at a CertainPath event"
                className="lp-brandon-img"
                role="img"
                style={brandonStyle}
              />
            </div>

            <div className="lp-coach-badge">
              <div className="av">BM</div>
              <div>
                <strong>Brandon Marshall</strong>
                <span>Lion’s Pride Coach</span>
              </div>
            </div>

            <div className="lp-stat-chip">
              <div className="v">
                $3M<em>→</em>$15M
              </div>
              <div className="l">Company growth led by Brandon</div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHAT IS LION'S PRIDE ================= */}
      <section className="lp-why-use" id="why">
        <div className="lp-why-use-shell">
          <h2>What is Lion’s Pride?</h2>
          <div>
            <p className="lead">
              Leading people is the skill that makes everything else work —{" "}
              <strong>
                and it’s the one most owners were never trained for.
              </strong>
            </p>
            <p className="body">
              Lion’s Pride moves leadership beyond instinctual reactions and
              into consistent, repeatable habits that hold up in both calm and
              chaos. Each month you receive a challenge, apply it with your
              team, then teach the framework using the included Leader Guide —
              creating compounding development across your whole organization.
            </p>
          </div>
        </div>
      </section>

      {/* ================= WHY CHOOSE — BUILT-IN ADVANTAGES ================= */}
      <section className="lp-why-choose" id="advantages">
        <div className="lp-wrap">
          <div className="lp-why-choose-head">
            <div className="lp-trust-pill">
              <ShieldIcon strokeWidth={2.2} />
              Built-in advantages
            </div>
            <h2>Why teams choose Lion’s Pride</h2>
            <p>
              Practical structure for turning leadership into lasting habits
              across your organization.
            </p>
          </div>

          <div aria-hidden="true" className="lp-path-feature">
            <div className="lp-path-card">
              <div className="lp-path-head">
                <span className="t">Your leadership path</span>
                <span className="s">Monthly challenge</span>
              </div>
              <div className="lp-path-body">
                <div className="lp-ring">
                  {/* 12 segments, r=80, circumference 502.65, segment 41.89 (37.89 dash + 4 gap) */}
                  <svg viewBox="0 0 200 200">
                    <g
                      fill="none"
                      strokeLinecap="butt"
                      strokeWidth="14"
                      transform="rotate(-90 100 100)"
                    >
                      <circle
                        cx="100"
                        cy="100"
                        r="80"
                        stroke="rgba(255,255,255,.09)"
                        strokeDasharray="37.89 4"
                      />
                      <circle
                        cx="100"
                        cy="100"
                        r="80"
                        stroke="#FFC857"
                        strokeDasharray="37.89 4 37.89 4 37.89 4 37.89 1000"
                      />
                      <circle
                        className="now"
                        cx="100"
                        cy="100"
                        r="80"
                        stroke="#B20030"
                        strokeDasharray="0 167.56 37.89 1000"
                      />
                    </g>
                  </svg>
                  <div className="lp-ring-center">
                    <div className="n">12</div>
                    <div className="l">months</div>
                  </div>
                </div>

                <div>
                  <div className="lp-loop-title">Every month</div>
                  <ol className="lp-loop">
                    <li>
                      <span className="step">1</span>Receive the challenge
                    </li>
                    <li className="active">
                      <span className="step">2</span>Apply it with your team
                    </li>
                    <li>
                      <span className="step">3</span>Teach it forward
                    </li>
                  </ol>
                </div>

                <div className="lp-topics">
                  <span>Difficult conversations</span>
                  <span>Alignment</span>
                  <span>Accountability</span>
                  <span>Trust</span>
                </div>
              </div>
            </div>
            <div className="lp-guide-card">
              <div className="lp-guide-top">
                <div className="doc">
                  <BookOpenIcon />
                </div>
                <div>
                  <strong>Leader Guide</strong>
                  <span>Run this session with your team</span>
                </div>
              </div>
              <div className="lp-guide-lines">
                <div className="lp-guide-line">
                  <span className="ck">
                    <CheckIcon strokeWidth={4} />
                  </span>
                  Learn the framework
                </div>
                <div className="lp-guide-line">
                  <span className="ck">
                    <CheckIcon strokeWidth={4} />
                  </span>
                  Apply it in the day-to-day
                </div>
                <div className="lp-guide-line todo">
                  <span className="ck" />
                  Teach it to your team
                </div>
              </div>
            </div>
          </div>

          <div className="lp-why-cards" data-stagger>
            {WHY_CARDS.map((card) => (
              <div className="lp-why-cell" key={card.title}>
                <div className="lp-why-card">
                  <div className="lp-why-card-icon">{card.icon}</div>
                  <h3>{card.title}</h3>
                  <p>{card.body}</p>
                  <div className="tag">{card.tag}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FORMATS ================= */}
      <section className="lp-formats-sec" id="formats">
        <div className="lp-wrap">
          <div className="lp-formats-head">
            <h2>Two ways to join Lion’s Pride</h2>
            <p>
              Lead alongside a group of fellow members, or build a leadership
              journey around you.
            </p>
          </div>

          <div className="lp-formats-grid" data-stagger>
            <div className="lp-fmt">
              <div className="lp-fmt-icon">
                <UsersIcon />
              </div>
              <h3>Group-Based</h3>
              <p>
                Build your leadership skills in small group training with your
                fellow members. Each month brings one leadership challenge — a
                difficult conversation, an alignment problem, an accountability
                gap — that you work through with a cohort of owners and managers
                facing the same things in their own businesses.
              </p>
              <p>
                You apply it with your team using the Leader Guide, then come
                back the next month with what worked and what didn’t. Twelve
                months of that turns leadership from instinct into habit — for
                you and for the people you lead.
              </p>
              <FormatList
                items={[
                  "12-month cohort",
                  "Monthly challenges and Leader Guide",
                  "Community of committed leaders",
                ]}
              />
              <div className="lp-fmt-avail">
                <FormatTiers kinds={["add", "add", "add", "inc"]} />
                <p className="lp-tier-note">
                  Included with <strong>Scale</strong>; available as an add-on
                  at every other stage.
                </p>
              </div>
            </div>

            <div className="lp-fmt dark-card">
              <div className="lp-fmt-icon">
                <UserIcon />
              </div>
              <h3>Individual-Based</h3>
              <p>
                Customize your leadership journey to match the needs of your
                business and life. Instead of moving through the challenges with
                a cohort, you work one-on-one with Brandon Marshall on the
                leadership situations you are facing right now — the manager who
                isn’t stepping up, the conversation you keep postponing, the
                handoff from doing the work to leading the work.
              </p>
              <p>
                The same frameworks apply, but the order, the pace, and the
                emphasis are built around you and the business you are running.
              </p>
              <FormatList
                items={[
                  "One-on-one mentorship with Brandon",
                  "Built around your goals and your team",
                  "Paced to fit how you work",
                ]}
              />
              <div className="lp-fmt-avail">
                <FormatTiers kinds={["add", "add", "add", "add"]} />
                <p className="lp-tier-note">
                  Available as an add-on at <strong>every stage</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section className="lp-hiw" id="how-it-works">
        <div className="lp-wrap">
          <div className="lp-hiw-head">
            <h2>How it works</h2>
            <p>A clear path from first conversation to consistent results</p>
          </div>

          <ol className="lp-hiw-grid" data-stagger>
            {HOW_IT_WORKS.map((step, i) => (
              <li className="lp-hiw-card" key={step.title}>
                <div className="lp-hiw-num">{i + 1}</div>
                <div className="lp-hiw-body">
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="lp-hiw-foot">
            <button className="lp-btn" onClick={openChat} type="button">
              Apply to Lion’s Pride <span className="arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* ================= MENTOR ================= */}
      <section className="lp-story-sec" id="success-stories">
        <div className="lp-wrap">
          <div className="lp-story-head">
            <h2>Your Lion’s Pride Mentor</h2>
          </div>

          <figure className="lp-story-card">
            <div aria-hidden="true" className="lp-mentor-photo">
              <div className="lp-brandon-img flip" style={brandonStyle} />
            </div>

            <div className="lp-story-quote">
              <p className="mentor-bio">
                Brandon Marshall is a seasoned leader and mentor with over 20
                years of experience. His background includes growing revenues
                from $3M to nearly $15M in 7 years in the linen and uniform
                industry, then leading a commercial HVAC team to record-breaking
                sales and doubling revenue in his first year as General Manager.
                Today, as CertainPath’s Transformation &amp; Leadership Mentor,
                he helps leaders unlock their potential and lead with purpose.
              </p>
            </div>

            <div className="lp-story-stats">
              <div className="lp-sstat">
                <div className="v">20+</div>
                <div className="l">Years leading</div>
              </div>
              <div className="lp-sstat">
                <div className="v">
                  $3M<span className="to">→</span>$15M
                </div>
                <div className="l">Co. growth driven</div>
              </div>
            </div>
          </figure>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="lp-faq-sec" id="faq">
        <div className="lp-wrap">
          <div className="lp-faq-head">
            <h2>Frequently asked questions</h2>
            <p>Everything you need to know about Lion’s Pride</p>
          </div>

          <div className="lp-faq-list" data-stagger>
            {FAQS.map((faq) => (
              <details className="lp-faq-item" key={faq.question}>
                <summary>
                  {faq.question}
                  <span className="lp-faq-chevron">
                    <ChevronDownIcon />
                  </span>
                </summary>
                <div className="lp-faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="lp-fcta-sec" id="apply">
        <div className="lp-wrap">
          <div className="lp-fcta-shell">
            <div aria-hidden="true" className="lp-fcta-icon">
              <CrownIcon />
            </div>
            <h2>Become the leader your team is waiting for</h2>
            <p>
              Apply to a Lion’s Pride cohort and start building the leadership
              habits that scale with your business.
            </p>
            <button className="lp-fcta-btn" onClick={openChat} type="button">
              Apply to Lion’s Pride <span className="arrow">→</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  </CpLandingShell>
);

export default LionsPridePage;
